import { useState, useEffect, useRef } from 'react';

/**
 * Hook to track network connectivity status on desktop.
 * Returns { isOnline, showBanner, bannerType }.
 * - showBanner: true when the banner should be visible
 * - bannerType: 'offline' | 'back-online'
 * The "back online" banner auto-hides after 3 seconds.
 */
export default function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerType, setBannerType] = useState<'offline' | 'back-online' | null>(null);
  const [reconnectCount, setReconnectCount] = useState(0);
  const wasOfflineRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Check initial state — window events only fire on changes, not on mount
    if (!navigator.onLine) {
      setIsOnline(false);
      setBannerType('offline');
      setShowBanner(true);
      wasOfflineRef.current = true;
    }

    const handleOffline = () => {
      setIsOnline(false);
      setBannerType('offline');
      setShowBanner(true);
      wasOfflineRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleOnline = () => {
      setIsOnline(true);
      if (wasOfflineRef.current) {
        setReconnectCount(c => c + 1);
        setBannerType('back-online');
        setShowBanner(true);
        wasOfflineRef.current = false;
        timerRef.current = setTimeout(() => {
          setShowBanner(false);
          setBannerType(null);
        }, 3000);
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { isOnline, showBanner, bannerType, reconnectCount };
}
